import { useRef } from "react";
import {
  ThreadPrimitive,
  MessagePrimitive,
  useMessage,
  ComposerPrimitive,
} from "@assistant-ui/react";
import SendIcon from "../../../assets/icons/SendIcon";
import { useScrollDetection } from "../../../hooks/useScrollDetection";
import { ToolStatusBar } from "../VLChat/ToolStatusBar";
import { useToolStatus } from "../VLChat/VLChatWrapper";
import MarkdownText from "./MarkdownText/MarkdownText";
import MessageActions from "./MessageActions/MessageActions";
import ThreadWelcome from "./ThreadWelcome/ThreadWelcome";
import ThreadHeader from "./ThreadHeader/ThreadHeader";
import AnimatedMessage from "./AnimatedMessage/AnimatedMessage";
import styles from "./ChatView.module.scss";

// ============================================================================
// Message Components
// ============================================================================

const UserMessage = () => {
  const message = useMessage();
  return (
    <AnimatedMessage key={message.id}>
      <MessagePrimitive.Root className={styles.userMessage}>
        <div className={styles.userMessageContent} dir="auto">
          <MessagePrimitive.Parts />
        </div>
      </MessagePrimitive.Root>
    </AnimatedMessage>
  );
};

const AssistantMessage = () => {
  const { toolStatuses, showToolStatus, isThinking } = useToolStatus();
  const message = useMessage();

  // Only show tool status on the last assistant message
  // showToolStatus is controlled by our streaming logic and cleared after completion
  // Don't depend on thread.isRunning as it may lag behind due to async store updates
  const isLastMessage = message.isLast;
  const shouldShowToolStatus = (showToolStatus || isThinking) && isLastMessage;

  return (
    <AnimatedMessage
      key={message.id}
      delay={0.5} // subtle delay for assistant
    >
      <MessagePrimitive.Root className={styles.assistantMessage}>
        {shouldShowToolStatus && (
          <ToolStatusBar
            toolStatuses={toolStatuses}
            visible={true}
            isThinking={isThinking}
          />
        )}
        <div className={styles.assistantMessageContent} dir="auto">
          <MessagePrimitive.Parts components={{ Text: MarkdownText }} />
        </div>
        <MessageActions />
      </MessagePrimitive.Root>
    </AnimatedMessage>
  );
};

const Composer = () => {
  return (
    <ComposerPrimitive.Root className={styles.composer}>
      <ComposerPrimitive.Input
        placeholder="Ask me anything about your dataset"
        className={styles.composerInput}
        rows={3}
        autoFocus
        dir="auto"
        data-chat-composer-input
      />
      <ThreadPrimitive.If running={false}>
        <ComposerPrimitive.Send className={styles.sendButton}>
          <SendIcon />
        </ComposerPrimitive.Send>
      </ThreadPrimitive.If>
      <ThreadPrimitive.If running={true}>
        <ComposerPrimitive.Send className={styles.sendButton} disabled>
          <SendIcon />
        </ComposerPrimitive.Send>
      </ThreadPrimitive.If>
    </ComposerPrimitive.Root>
  );
};

// ============================================================================
// Main Chat View Component
// ============================================================================

export default function ChatView() {
  // Scroll detection hook
  const { scrolled, scrollElementRef: viewportRef } =
    useScrollDetection<HTMLDivElement>();
  const threadRootRef = useRef<HTMLDivElement>(null);

  return (
    <div className={styles.threadContainer}>
      <ThreadHeader />
      <ThreadPrimitive.Root
        ref={threadRootRef}
        className={`${styles.threadRoot} ${scrolled ? styles.scrolled : ""}`}
      >
        <ThreadPrimitive.Viewport
          ref={viewportRef}
          className={styles.threadViewport}
          data-aui-thread-viewport
        >
          <ThreadPrimitive.Empty>
            <ThreadWelcome />
          </ThreadPrimitive.Empty>

          <div className={styles.messagesContainer}>
            <ThreadPrimitive.Messages
              components={{
                UserMessage,
                AssistantMessage,
              }}
            />
          </div>
        </ThreadPrimitive.Viewport>

        <div className={styles.composerWrapper}>
          <Composer />
        </div>
      </ThreadPrimitive.Root>
    </div>
  );
}
